import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { usePresentationStore } from '@/stores/presentationStore'
import { useJourneyStore } from '@/stores/journeyStore'
import { 
  Users, 
  ArrowRight, 
  AlertCircle,
  CheckCircle,
  Clock,
  User
} from 'lucide-react'

export function JoinSessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const [participantName, setParticipantName] = useState('')
  const [isJoining, setIsJoining] = useState(false)
  const [joinError, setJoinError] = useState('')

  const { 
    joinSession, 
    currentSession, 
    isParticipantMode,
    activeSessions 
  } = usePresentationStore()
  
  const { initializeUser } = useJourneyStore()

  // Find the session
  const session = activeSessions.find(s => s.id === sessionId)

  useEffect(() => {
    // If already in participant mode and in this session, redirect to dashboard
    if (isParticipantMode && currentSession?.id === sessionId) {
      navigate('/')
    }
  }, [isParticipantMode, currentSession, sessionId, navigate])

  const handleJoinSession = async () => {
    if (!sessionId || !participantName.trim()) {
      setJoinError('Please enter your name')
      return
    }

    setIsJoining(true)
    setJoinError('')

    try {
      const success = joinSession(sessionId, participantName.trim())
      
      if (success) {
        // Initialize user profile for the journey
        await initializeUser({
          name: participantName.trim(),
          currentAIExperience: 'none' // Default for session participants
        })
        
        // Session started - timer functionality removed
        navigate('/')
      } else {
        setJoinError('Failed to join session. The session may be full or no longer active.')
      }
    } catch (error) {
      setJoinError('An error occurred while joining the session.')
    } finally {
      setIsJoining(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleJoinSession()
    }
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 bg-red-100 dark:bg-red-900/20 rounded-full w-16 h-16 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle>Session Not Found</CardTitle>
            <CardDescription>
              The session you're trying to join doesn't exist or is no longer active.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!session.isLive) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 bg-orange-100 dark:bg-orange-900/20 rounded-full w-16 h-16 flex items-center justify-center">
              <Clock className="w-8 h-8 text-orange-600 dark:text-orange-400" />
            </div>
            <CardTitle>Session Ended</CardTitle>
            <CardDescription>
              This presentation session has ended. Contact the presenter for access to a new session.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="flex justify-between">
                <span>Session:</span>
                <span className="font-medium">{session.title}</span>
              </div>
              <div className="flex justify-between">
                <span>Participants:</span>
                <span className="font-medium">{session.participants.length}</span>
              </div>
            </div>
            <Button onClick={() => navigate('/')} className="w-full mt-4">
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 p-3 bg-green-100 dark:bg-green-900/20 rounded-full w-16 h-16 flex items-center justify-center">
            <Users className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <CardTitle className="text-2xl">Join Presentation</CardTitle>
          <CardDescription>
            You're about to join an interactive AI journey presentation
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Session Info */}
          <div className="p-4 bg-muted/50 rounded-lg space-y-3">
            <h3 className="font-semibold">{session.title}</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span>{session.participants.length} participants</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span>Live session</span>
              </div>
            </div>
          </div>

          {/* Join Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Your Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  value={participantName}
                  onChange={(e) => setParticipantName(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter your name"
                  className="w-full pl-10 pr-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={isJoining}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                This will be visible to the presenter and other participants
              </p>
            </div>

            {joinError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {joinError}
                </p>
              </div>
            )}

            <Button 
              onClick={handleJoinSession}
              disabled={!participantName.trim() || isJoining}
              className="w-full gap-2"
            >
              {isJoining ? (
                <>Joining Session...</>
              ) : (
                <>
                  Join AI Journey <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>

          {/* What to Expect */}
          <div className="border-t pt-4">
            <h4 className="font-medium mb-3">What to expect:</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                Interactive AI experiences across 5 progressive stages
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                Hands-on practice with real AI tools and prompting
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                Personalized recommendations based on your journey
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                Estimated time: 45-60 minutes total
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}